import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { encodeAdminPath } from "@/lib/adminRoutes";

import { isAdminPortal } from "@/lib/domain";

const LoadingScreen = () => (
  <div
    data-testid="auth-loading-screen"
    className="min-h-screen flex items-center justify-center bg-background"
  >
    <div className="flex flex-col items-center gap-3">
      <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      <span className="overline">Loading</span>
    </div>
  </div>
);

const APPROVER_ROLES = ["approver_l1", "approver_l2", "approver_l3", "approver_all"];
const ALL_ADMIN_ROLES = ["admin", ...APPROVER_ROLES];

// Custom roles slug-encode their side as `custom_admin_*` or `custom_partner_*`
const isCustomAdmin = (role) => typeof role === "string" && role.startsWith("custom_admin_");
const isCustomPartner = (role) => typeof role === "string" && role.startsWith("custom_partner_");

export const isApprover = (user) => APPROVER_ROLES.includes(user?.role);
export const isAdminLike = (user) => ALL_ADMIN_ROLES.includes(user?.role) || isCustomAdmin(user?.role);
export const isPartnerLike = (user) => user?.role === "partner" || isCustomPartner(user?.role);

export const homeForUser = (user) => {
  if (!user) return "/login";
  const onAdmin = isAdminPortal();
  // Admin on partner portal → go to partner dashboard
  if ((user.role === "admin" || isCustomAdmin(user.role)) && !onAdmin) return "/dashboard";
  if (user.role === "admin" || isCustomAdmin(user.role)) return encodeAdminPath("/admin");
  if (isApprover(user)) return encodeAdminPath("/admin/approvals");
  if (user.role === "channel_partner") return "/channel/dashboard";
  if (user.role === "user") return "/become-retailer";
  return "/dashboard";
};

export const ProtectedRoute = ({ children, requireRole, requireRoles, requireKyc, requireRetailer }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;

  const allowedRoles = requireRoles || (requireRole ? [requireRole] : null);
  if (allowedRoles) {
    // Built-in role match OR custom-admin counts as "admin", custom-partner counts as "partner"
    const matches =
      allowedRoles.includes(user.role) ||
      (allowedRoles.includes("admin") && isCustomAdmin(user.role)) ||
      (allowedRoles.includes("partner") && isCustomPartner(user.role));
    if (!matches) return <Navigate to={homeForUser(user)} replace />;
  }
  // Phase 7.30e — requireKyc is now a no-op. KYC is verified by the parent
  // system (hitch.zone) at signup/login and at transaction time; we don't
  // force users through a local KYC form anymore. Kept as a param so the
  // route table doesn't need a refactor.
  void requireKyc;
  if (requireRetailer && user.role === "user") {
    return <Navigate to="/become-retailer" replace />;
  }
  return children;
};

export const PublicOnlyRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingScreen />;
  if (user) {
    // Honor ?next= when present and same-origin so post-login deep-links
    // (e.g. Landing's category cards → /login?next=/bbps/v2?...) actually land
    // where the user intended instead of always bouncing to the role's home.
    const params = new URLSearchParams(location.search || "");
    const next = params.get("next");
    if (next && next.startsWith("/") && !next.startsWith("//")) {
      return <Navigate to={next} replace />;
    }
    return <Navigate to={homeForUser(user)} replace />;
  }
  return children;
};
